import { useMemo } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { Metrics } from 'components/insights/constants';
import { formatDecimal, formatWholeNumber } from 'components/insights/formatting';
import { colors } from 'styles/theme/colors';

import MetricDetailLayout from '../../../shared/slideout/MetricDetailLayout';
import { readGrandTotal, readGrandTotalOrZero } from '../../../shared/slideout/rollupHelpers';
import { StatTile } from '../../../shared/slideout/statTiles';
import { METRIC_CONFIGS, NetworkPerformanceMetrics } from '../constants';
import { type MetricHeaderProps } from '../NetworkPerformanceSlideout';

import { buildColumnChartOptions, extractEntityMetricMap, withReferenceLine, withXAxisSublabel } from './chartHelpers';

export default function EngagementsHeader({
  loading,
  rollups,
  highestEntity,
  lowestEntity,
  entityRows,
  entityLabel,
}: MetricHeaderProps) {
  const overallEngagements = readGrandTotal(rollups, Metrics.ACTIVITIES_PER_PATIENT_PER_WEEK);
  const totalPatients = readGrandTotalOrZero(rollups, Metrics.ID_COUNT);

  const patientCountByName = useMemo(() => extractEntityMetricMap(rollups, Metrics.ID_COUNT), [rollups]);

  const { options, scrollConfig: baseScrollConfig } = useMemo(
    () =>
      buildColumnChartOptions({
        entityRows,
        seriesName: METRIC_CONFIGS[NetworkPerformanceMetrics.ENGAGEMENTS].label,
        color: colors.blue100,
        dataLabelsFormatter: function () {
          return formatDecimal(this.y ?? null);
        },
      }),
    [entityRows]
  );

  const avgValue = overallEngagements ?? 0;
  const chartOptions = useMemo(() => {
    const withRef = withReferenceLine(options, {
      value: avgValue,
      label: `Average (${formatDecimal(overallEngagements)})`,
    });
    return withXAxisSublabel(withRef, patientCountByName, 'Patients');
  }, [options, avgValue, overallEngagements, patientCountByName]);
  const scrollConfig = { minWidth: baseScrollConfig.minWidth };

  return (
    <MetricDetailLayout
      tiles={
        <>
          <StatTile
            label={METRIC_CONFIGS[NetworkPerformanceMetrics.ENGAGEMENTS].label}
            loading={loading}
            primary={formatDecimal(overallEngagements)}
            addendum={`Based on ${formatWholeNumber(totalPatients)} patients`}
          />
          <StatTile
            label='Most Engaged'
            loading={loading}
            primary={highestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={
              highestEntity ? `${formatDecimal(highestEntity.value)} Engagements Per Patient Per Week` : undefined
            }
            tone='green'
          />
          <StatTile
            label='Least Engaged'
            loading={loading}
            primary={lowestEntity?.name ?? '\u2014'}
            size='medium'
            addendum={
              lowestEntity ? `${formatDecimal(lowestEntity.value)} Engagements Per Patient Per Week` : undefined
            }
            tone='red'
          />
        </>
      }
      chartTitle={`${METRIC_CONFIGS[NetworkPerformanceMetrics.ENGAGEMENTS].label} by ${entityLabel}`}
      chartLoading={loading}
      isEmpty={entityRows.length === 0}
      scrollConfig={scrollConfig}
      emptyMessage='No engagement data to show for this time period.'>
      <HighchartsReact highcharts={Highcharts} options={chartOptions} />
    </MetricDetailLayout>
  );
}
